import {Box, HStack, IconButton, Text, Tooltip, useToast} from '@chakra-ui/react';
import {useEffect, useState} from "react";
import {playEmergencyBell, toggleBellSwitch} from "../../views/Dashboard/services";
import {FaBell, FaFireAlt, FaFlag} from "react-icons/fa";
import {FiPower} from 'react-icons/fi'; // General icons
import {BiAlarmExclamation} from "react-icons/bi";


const RemoteScreen = () => {
    const [timezone, setTimezone] = useState("");
    const [utcOffset, setUtcOffset] = useState("");

    useEffect(() => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimezone(tz);

        // Get UTC offset in hours and minutes
        const offsetMinutes = new Date().getTimezoneOffset();
        const sign = offsetMinutes <= 0 ? "+" : "-";
        const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, '0');
        const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, '0');
        setUtcOffset(`${sign}${hours}:${minutes}`);
    }, []);

    return (
        <>
            <Text fontSize={{base: "sm", md: "md"}}>Digital Bell System</Text>

            {/* Timezone Info */}
            <Tooltip label={`UTC Offset: ${utcOffset}`} fontSize="md">
                <Text fontSize="sm" color="blue.300" mt={2}>
                    Timezone: {timezone}
                </Text>
            </Tooltip>
        </>
    );
};

const DigitalBellRemote = () => {
    const [bellSwitch, setBellSwitch] = useState(Boolean(localStorage.getItem("isOn")));
    const [remoteEmergency, setRemoteEmergency] = useState(false);
    const toast = useToast();

    const handleBellSwitchChange = () => {
        const toggle = !bellSwitch;
        setBellSwitch(toggle);
        localStorage.setItem("isOn", toggle);

        try {
            const request = {isEnabled: bellSwitch};
            toggleBellSwitch(request);
        } catch (e) {
            console.error("Error ::", e);
        }
        toast({
            title: bellSwitch ? "Bell turned OFF" : "Bell turned ON",
            status: bellSwitch ? "warning" : "success",
            duration: 2000,
            isClosable: true,
        });
    };

    const handleEmergencyButton = async (type) => {
        const emergencyConfig = {
            fire: {
                message: "Fire Emergency Activated",
                emergencyType: "FIRE",
            },
            emergency: {
                message: "Emergency Sound Activated",
                emergencyType: "EMERGENCY",
            },
            nationalAnthem: {
                message: "National Anthem Started",
                emergencyType: "NATIONAL_ANTHEM",
                status: "info",
            },
            instantBell: {
                message: "Instant Bell Played",
                emergencyType: "INSTANT_BELL",
                status: "success",
            },
        };
        const selectedEmergency = emergencyConfig[type] || emergencyConfig.emergency;

        setRemoteEmergency(!remoteEmergency);
        toast({
            title: selectedEmergency.message,
            description: "Playing sound from frontend bells folder.",
            status: selectedEmergency.status || "error",
            duration: 2000,
            isClosable: true,
        });

        try {
            const requestDto = {
                emergencyType: selectedEmergency.emergencyType
            }
            await playEmergencyBell(requestDto);
        } catch (e) {
            console.log(e);
        }
    };

    return (
        <Box
            p={{base: 3, md: 5}}
            w="100%"
            maxW="100%"
            borderWidth="1px"
            borderRadius="lg"
            overflow="hidden"
            bg="gray.900"
            color="white"
        >
            <Box
                mb={4}
                p={3}
                borderWidth="2px"
                borderRadius="md"
                borderColor="gray.600"
                bg="gray.700"
                color="teal.200"
                textAlign="center"
                fontSize="lg"
                fontFamily="monospace"
                position="relative"
            >
                <RemoteScreen/>

                <IconButton
                    aria-label={bellSwitch ? "Turn OFF" : "Turn ON"}
                    icon={<FiPower/>}
                    colorScheme={bellSwitch ? "red" : "green"}
                    size="md"
                    borderRadius="full"
                    position="absolute"
                    top="2"
                    right="2"
                    onClick={handleBellSwitchChange}
                />
            </Box>

            {/* Additional Emergency Buttons */}
            <HStack spacing={{base: 2, md: 4}} justify="center" mt={4} flexWrap="wrap">
                <Tooltip label="Fire Emergency" fontSize="md">
                    <IconButton
                        aria-label="Fire Emergency"
                        icon={<FaFireAlt/>}
                        colorScheme="orange"
                        size="lg"
                        onClick={() => handleEmergencyButton("fire")}
                    />
                </Tooltip>
                <Tooltip label="Emergency Sound" fontSize="md">
                    <IconButton
                        aria-label="Emergency Sound"
                        icon={<BiAlarmExclamation/>}
                        colorScheme="blue"
                        size="lg"
                        onClick={() => handleEmergencyButton("emergency")}
                    />
                </Tooltip>
                <Tooltip label="National Anthem" fontSize="md">
                    <IconButton
                        aria-label="National Anthem"
                        icon={<FaFlag/>}
                        colorScheme="yellow"
                        size="lg"
                        onClick={() => handleEmergencyButton("nationalAnthem")}
                    />
                </Tooltip>
                <Tooltip label="Instant Bell" fontSize="md">
                    <IconButton
                        aria-label="Instant Bell"
                        icon={<FaBell/>}
                        colorScheme="teal"
                        size="lg"
                        onClick={() => handleEmergencyButton("instantBell")}
                    />
                </Tooltip>
            </HStack>

        </Box>
    );
};

export default DigitalBellRemote;
